/*
 * @providesModule WithdrawalManager
 *
 * This module is responsible for managing the withdrawal flow in the application.
 * It aims to replace the current WithdrawFlowController and handle the withdrawal process
 */

import ClientPropertyConstants from 'ClientPropertyConstants';
import WithdrawFlowController from 'WithdrawFlowController';
import {WithdrawalOptionScreen} from './WithdrawalOptionsScreen';
import NavigationManager from 'NavigationManager';
import SMSVerification from 'SMSVerification';
import {TouchableOpacity, NativeModules, Platform} from 'react-native';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import * as BankWithdrawalController from 'BankWithdrawalController';
import {EBankWithdrawalOptions} from './BankWithdrawal/BankWithdrawalConstants';
import CurrentUserStore from 'CurrentUserStore';
import LoadingIndicator from 'LoadingIndicator';
import PaymentsAPIController from 'PaymentsAPIController';
import {getServerUserPropertiesAsRecord} from 'UserPropertiesHelpers';
import {ServerPaymentsResponse} from 'StoreInfo';
import {AllUserPropertiesKeys} from 'UserPropertiesConstants';
import AWSManager, {ValidEvents} from 'AWSManager';
import {WITHDRAWAL_BANK_PHONE_VERIFICATION} from 'AWSConstants';
import {SkillzQueryClient} from '@api/ReactQueryClient';
import {QUERY_KEYS} from './BankWithdrawal/hooks';
import {ReactElement} from 'react';
import {Navigator} from 'react-native-deprecated-custom-components';

const {textColorPrimary} = SkillzStyle.fullStyles;
const SKZAPIManager = NativeModules.SKZAPIManager;

export enum StandardWithdrawalOptions {
  STANDARD_WITHDRAWAL = 'standard_withdrawal',
}

export type WithdrawalOptions = EBankWithdrawalOptions | StandardWithdrawalOptions;
export const WithdrawalOptions = {...EBankWithdrawalOptions, ...StandardWithdrawalOptions};

export type WithdrawalFlowOptions = {
  launchFromStandardWithdrawalPage?: boolean;
};

export class WithdrawalManager {
  async launchWithdrawalFlow(options: WithdrawalFlowOptions = {}) {
    SkillzQueryClient.invalidateQueries([QUERY_KEYS.paymentData]);

    LoadingIndicator.show();
    const userPaymentData = await SkillzQueryClient.fetchQuery({
      queryKey: [QUERY_KEYS.paymentData],
      queryFn: () =>
        PaymentsAPIController.getPaymentsInformation(false, SKZAPIManager.OnError.failSilently),
    })
      .catch((error: Object) => {
        console.error('WithdrawalManager: Error fetching payment data:', error);
        this.launchStandardWithdrawalFlow();
        throw error;
      })
      .finally(() => LoadingIndicator.hide());

    if (this.isBankWithdrawalEnabled(userPaymentData.payment_types.includes('AEROPAY'))) {
      const withdrawalUserProperties = (await getServerUserPropertiesAsRecord('withdrawal')) || {};
      const isACHDepositor = userPaymentData.aeropay?.user_has_deposited;
      const lastTimeSeenBankPopup = withdrawalUserProperties['withdrawal.lastTimeSeenBankPopup'];
      const isFirstTimeVisit = !lastTimeSeenBankPopup;
      const shouldGoDirectlyToBankWithdrawal = isACHDepositor && !isFirstTimeVisit;

      if (shouldGoDirectlyToBankWithdrawal) {
        const isRTPEnabled = this.isBankWithdrawalRTPEnabled();
        return BankWithdrawalController.displayBankWithdrawalScreen({
          withdrawalType: isRTPEnabled
            ? WithdrawalOptions.BANK_INSTANT_WITHDRAWAL
            : WithdrawalOptions.BANK_DEFAULT_WITHDRAWAL,
          launchFromStandardWithdrawalPage: options.launchFromStandardWithdrawalPage,
        });
      }
      return this.displayWithdrawalOptionsScreen(
        userPaymentData,
        withdrawalUserProperties,
        options.launchFromStandardWithdrawalPage
      );
    }

    return this.launchStandardWithdrawalFlow();
  }

  launchStandardWithdrawalFlow() {
    WithdrawFlowController.displayWithdrawFlow();
  }

  private isBankWithdrawalEnabled(isAeropayAvailable: boolean): boolean {
    return (
      ClientPropertyConstants.getPlayerWithdrawalAeropayEnabled() &&
      isAeropayAvailable &&
      Platform.OS !== 'web'
    );
  }

  isBankWithdrawalRTPEnabled(): boolean {
    return ClientPropertyConstants.getPlayerWithdrawalAeropayRTPEnabled();
  }

  private async displayWithdrawalOptionsScreen(
    userPaymentData: ServerPaymentsResponse,
    withdrawalUserProperties: Record<AllUserPropertiesKeys, string>,
    launchFromStandardWithdrawalPage: boolean = false
  ) {
    if (this.isBankWithdrawalEnabled(userPaymentData?.payment_types.includes('AEROPAY'))) {
      if (launchFromStandardWithdrawalPage) {
        NavigationManager.pushSceneWithHomeAsRoot({
          scene: 'WithdrawalOptionScreen',
          render: (): ReactElement => {
            return (
              <WithdrawalOptionScreen
                hasBackButton={true}
                onBackNavigation={(): void => NavigationManager.pop()}
              />
            );
          },
          configure: (): Navigator.SceneConfigs => Navigator.SceneConfigs.HorizontalSwipeJump,
        });
        return;
      }

      NavigationManager.pushComponentWithScene(
        <WithdrawalOptionScreen
          hasBackButton={true}
          onBackNavigation={(): void => NavigationManager.pop()}
        />,
        true,
        'WithdrawalOptionScreen'
      );

      const hasLinkedBanks = !!userPaymentData?.bank_accounts?.length;
      const isRTPEnabled = this.isBankWithdrawalRTPEnabled();
      const canShowBankWithdrawalPopup = BankWithdrawalController.canShowBankWithdrawalPopup(
        userPaymentData,
        withdrawalUserProperties
      );

      canShowBankWithdrawalPopup &&
        BankWithdrawalController.showBankWithdrawalPopup(
          hasLinkedBanks,
          isRTPEnabled,
          userPaymentData
        );
    }
  }

  async showSMSVerification(callback: (success: boolean) => void) {
    const rightIconStyles = {
      height: 18,
      width: 18,
      marginRight: 16,
    };

    const userDetails = await CurrentUserStore.fetchUserPersonalInfo(false);
    const SMSVerificationComponent = (
      <SMSVerification
        showCloseButton={true}
        rightIconComponent={
          <TouchableOpacity style={rightIconStyles} onPress={() => callback(false)}>
            <SkillzImage
              source={'close_button_icon.svg'}
              style={rightIconStyles}
              tintColor={textColorPrimary.color}
            />
          </TouchableOpacity>
        }
        onClose={() => {
          this.trackAWSWithdrawalEvent(WITHDRAWAL_BANK_PHONE_VERIFICATION, {ACTION: 'CANCELED'});
          callback(false);
        }}
        // this is the callback that should be called when the verification is successful
        onDone={() => {
          this.trackAWSWithdrawalEvent(WITHDRAWAL_BANK_PHONE_VERIFICATION, {ACTION: 'VERIFIED'});
          callback(true);
        }}
        clientProps={{
          header: ClientPropertyConstants.getSMSPromptDepositHeader(),
          body: ClientPropertyConstants.getSMSPromptDepositBody(),
        }}
        disableEdit={userDetails?.phone_number ? true : false}
        phoneNumber={userDetails?.phone_number}
        phoneNumberWithCountryCode={userDetails?.phone_number}
      />
    );
    NavigationManager.pushComponentWithScene(SMSVerificationComponent, true, 'SMSVerification');
  }

  async trackAWSWithdrawalEvent(eventName: ValidEvents, body: object) {
    const currentUserId = await CurrentUserStore.getCurrentUserId();
    const awsData = JSON.stringify({...body, USER_ID: currentUserId});
    AWSManager.trackEvent(eventName, {result: awsData});
  }
}

export default new WithdrawalManager();
